import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { getConsent, setConsent, hasConsented, type CookiePreferences } from "@/utils/cookieConsent";
import { loadAnalytics, pushConsent } from "@/utils/dataLayer";

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!hasConsented()) {
      pushConsent(false, false);
      const timer = setTimeout(() => {
        setVisible(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setReady(true));
        });
      }, 10000);
      return () => clearTimeout(timer);
    } else {
      const prefs = getConsent();
      if (prefs.analytics || prefs.marketing) {
        loadAnalytics();
      }
      pushConsent(prefs.analytics, prefs.marketing);
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      const prefs = getConsent();
      setAnalytics(prefs.analytics);
      setMarketing(prefs.marketing);
      setShowPrefs(true);
      setVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setReady(true));
      });
    };
    window.addEventListener("open-cookie-settings", handler);
    return () => window.removeEventListener("open-cookie-settings", handler);
  }, []);

  const dismiss = () => {
    setReady(false);
    setTimeout(() => setVisible(false), 500);
  };

  const handleAcceptAll = () => {
    setConsent({ analytics: true, marketing: true, accepted: true });
    loadAnalytics();
    pushConsent(true, true);
    dismiss();
  };

  const handleReject = () => {
    setConsent({ analytics: false, marketing: false, accepted: true });
    pushConsent(false, false);
    dismiss();
  };

  const handleSavePrefs = () => {
    setConsent({ analytics, marketing, accepted: true });
    if (analytics || marketing) {
      loadAnalytics();
    }
    pushConsent(analytics, marketing);
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-[60] border-t border-void-border-strong bg-void-surface text-white transition-transform duration-500 ${
        ready ? "translate-y-0" : "translate-y-full"
      }`}
    >
      <div className="max-w-industrial mx-auto px-6 lg:px-10 py-5">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
            <p className="font-mono text-xs text-neutral-400 leading-relaxed flex-1">
              THIS SITE USES COOKIES FOR ANALYTICS AND MARKETING PURPOSES.
              BY CONTINUING, YOU AGREE TO OUR{" "}
              <Link to="/privacy" className="text-primary hover:underline">
                PRIVACY POLICY
              </Link>
              .
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-3 shrink-0">
              <button
                onClick={() => setShowPrefs(!showPrefs)}
                className="font-mono text-xs font-bold uppercase tracking-wider text-neutral-400 hover:text-white transition-colors px-3 py-2 w-full sm:w-auto"
              >
                [ MANAGE ]
              </button>
              <button
                onClick={handleReject}
                className="font-mono text-xs font-bold uppercase tracking-wider px-5 py-2.5 border border-neutral-500 text-white hover:bg-neutral-800 transition-colors w-full sm:w-auto"
              >
                REJECT NON-ESSENTIAL
              </button>
              <button
                onClick={handleAcceptAll}
                className="font-mono text-xs font-bold uppercase tracking-wider px-5 py-2.5 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors w-full sm:w-auto"
              >
                ACCEPT ALL
              </button>
            </div>
          </div>

          {showPrefs && (
            <div className="border-t border-void-border-strong pt-4 pb-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="flex items-center justify-between gap-4 p-3 border border-void-border-strong bg-void-card">
                  <div>
                    <span className="font-mono text-xs font-bold uppercase tracking-wider block text-white">ESSENTIAL</span>
                    <span className="font-mono text-void-text-dim" style={{ fontSize: "0.65rem" }}>Required for site functionality</span>
                  </div>
                  <Switch checked disabled className="opacity-60" />
                </div>

                <div className="flex items-center justify-between gap-4 p-3 border border-void-border-strong bg-void-card">
                  <div>
                    <span className="font-mono text-xs font-bold uppercase tracking-wider block text-white">ANALYTICS</span>
                    <span className="font-mono text-void-text-dim" style={{ fontSize: "0.65rem" }}>Google Analytics</span>
                  </div>
                  <Switch checked={analytics} onCheckedChange={setAnalytics} />
                </div>

                <div className="flex items-center justify-between gap-4 p-3 border border-void-border-strong bg-void-card">
                  <div>
                    <span className="font-mono text-xs font-bold uppercase tracking-wider block text-white">MARKETING</span>
                    <span className="font-mono text-void-text-dim" style={{ fontSize: "0.65rem" }}>Meta Pixel, LinkedIn Insight</span>
                  </div>
                  <Switch checked={marketing} onCheckedChange={setMarketing} />
                </div>
              </div>
              <button
                onClick={handleSavePrefs}
                className="font-mono text-xs font-bold uppercase tracking-wider px-5 py-2.5 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors w-full sm:w-auto"
              >
                SAVE PREFERENCES
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
